import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { getDevice } from "@/lib/device";
import { chargeBankPos } from "@/lib/bankpos";
import { toast } from "sonner";
import { Wallet, Banknote, CreditCard, Loader2, Check, CheckCircle2, RefreshCw, DoorOpen, Utensils, ShieldAlert, QrCode, MessageSquare } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { fmtTL, fmtCur } from "@/components/StatusBadge";
import { cdPost } from "@/lib/customerDisplay";
import ApprovalDialog from "@/components/ApprovalDialog";

const CURRENCIES = ["TRY", "USD", "EUR", "GBP"];
const SYMBOL = { TRY: "₺", USD: "$", EUR: "€", GBP: "£" };
const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;

export default function PaymentDialog({ open, total, onClose, onConfirm, initialCollected = [], onPersist, onCancel, canVoid = false, mealCards = { enabled: false, providers: [] } }) {
  const [collected, setCollected] = useState([]);
  const [draft, setDraft] = useState({ method: "cash", currency: "TRY", amount: "" });
  const [rates, setRates] = useState(null);
  const [loadingRates, setLoadingRates] = useState(false);
  const [posSending, setPosSending] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [mealProvider, setMealProvider] = useState("");
  const [mealMode, setMealMode] = useState("qr");
  const [mealCode, setMealCode] = useState("");
  const [corrections, setCorrections] = useState([]);
  const [correctIdx, setCorrectIdx] = useState(null);
  const [approveOpen, setApproveOpen] = useState(false);
  const [qrRef, setQrRef] = useState("");
  const [mealCharging, setMealCharging] = useState(false);
  const [mealReceived, setMealReceived] = useState(null);

  const mealEnabled = mealCards?.enabled && (mealCards?.providers || []).length > 0;

  const loadRates = async () => {
    setLoadingRates(true);
    try { const { data } = await api.get("/fx"); setRates(data); } catch (e) {} finally { setLoadingRates(false); }
  };
  useEffect(() => {
    if (open) {
      loadRates();
      setCollected(initialCollected || []);
      setCorrections([]); setCorrectIdx(null); setApproveOpen(false);
      setDraft({ method: "cash", currency: "TRY", amount: "" });
      setPosSending(false);
      setQrRef("GRT" + Date.now().toString(36).toUpperCase());
      setMealProvider((mealCards?.providers || [])[0]?.id || "");
      setMealMode("qr");
      setMealCode("");
      setMealCharging(false);
      setMealReceived(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const warn = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [open]);

  const persist = (next) => { if (onPersist) onPersist(next); };
  const rateOf = (cur) => (cur === "TRY" ? 1 : (rates?.currencies?.[cur]?.used || 0));
  const draftAmt = parseFloat(draft.amount) || 0;
  const draftTry = round2(draftAmt * rateOf(draft.currency));

  const isMealTender = (c) => c.method === "meal_card";
  const cardTry = round2(collected.filter((c) => c.method === "card").reduce((s, c) => s + c.amountTry, 0));
  const qrTry = round2(collected.filter((c) => c.method === "qr").reduce((s, c) => s + c.amountTry, 0));
  const cashTry = round2(collected.filter((c) => c.method === "cash").reduce((s, c) => s + c.amountTry, 0));
  const mealBaseTry = round2(collected.filter(isMealTender).reduce((s, c) => s + (c.base || 0), 0));
  const mealSurcharge = round2(collected.filter(isMealTender).reduce((s, c) => s + (c.surcharge || 0), 0));
  const nonCashApplied = Math.min(round2(cardTry + qrTry + mealBaseTry), total);
  const cashOwed = round2(Math.max(0, total - nonCashApplied));
  const cashApplied = Math.min(cashTry, cashOwed);
  const paidApplied = round2(nonCashApplied + cashApplied);
  const remaining = round2(Math.max(0, total - paidApplied));
  const change = round2(Math.max(0, cashTry - cashOwed));
  const effectiveTotal = round2(total + mealSurcharge);
  const done = remaining <= 0.01;

  const markupOf = (pid) => {
    const p = (mealCards?.providers || []).find((x) => x.id === pid);
    return p ? Math.max(0, Number(p.markup_pct) || 0) / 100 : 0;
  };
  const mealBase = round2(remaining);
  const mealMk = markupOf(mealProvider);
  const mealSur = round2(mealBase * mealMk);
  const mealCharged = round2(mealBase + mealSur);

  useEffect(() => {
    if (!open) return;
    cdPost({
      mode: "pay", total: effectiveTotal,
      collected: collected.map((c) => ({ method: c.method, amountTry: c.amountTry, currency: c.currency, amount: c.amount })),
      paid: round2(cardTry + qrTry + cashTry + mealBaseTry + mealSurcharge), remaining, change, done,
    });
  }, [open, collected, effectiveTotal, remaining, change, done, cardTry, qrTry, cashTry, mealBaseTry, mealSurcharge]);

  const setD = (k, v) => setDraft((d) => ({ ...d, [k]: v, ...(k === "method" && v === "card" ? { currency: "TRY" } : {}) }));
  const fillRemaining = () => {
    const r = rateOf(draft.currency) || 1;
    setDraft((d) => ({ ...d, amount: String(round2(remaining / r)) }));
  };

  const commitCash = () => {
    if (draftTry <= 0) return;
    const next = [...collected, { method: "cash", currency: draft.currency, amount: draftAmt, amountTry: draftTry }];
    setCollected(next); persist(next);
    setDraft({ method: "cash", currency: "TRY", amount: "" });
  };
  const commitCard = async () => {
    if (draftTry <= 0 || posSending) return;
    // Karttan (nakit dışı) PARA ÜSTÜ VERİLMEZ — çekilecek tutar kalan borçla sınırlanır; fazlası yalnız nakitten.
    const chargeTry = round2(Math.min(draftTry, remaining));
    if (chargeTry <= 0) { toast.info("Kalan tahsilat yok — kart tutarı gerekmiyor."); return; }
    if (draftTry > remaining + 0.01) toast.info(`Karttan en fazla kalan tutar (${fmtTL(remaining)}) çekilir — para üstü yalnız nakitten verilir.`);
    setPosSending(true);
    try {
      const bp = getDevice()?.bankPos || {};
      const res = await chargeBankPos({ amount: chargeTry, config: bp });
      if (!res.approved) {
        toast.error(res.message || "Kart işlemi reddedildi");
        return;
      }
      const next = [...collected, { method: "card", currency: "TRY", amount: chargeTry, amountTry: chargeTry, posRef: res.ref, cardType: res.cardType, last4: res.last4 }];
      setCollected(next); persist(next);
      setDraft({ method: "cash", currency: "TRY", amount: "" });
    } finally {
      setPosSending(false);
    }
  };
  const commitMeal = async () => {
    if (mealBase <= 0 || !mealProvider || mealCharging) return;
    if (mealMode === "sms" && !mealCode.trim()) return;
    const prov = (mealCards?.providers || []).find((x) => x.id === mealProvider);
    setMealCharging(true);
    try {
      // Ödemeyi yemek kartı ENTEGRATÖRÜNDEN geçir; onay/red kasiyere döner.
      const { data } = await api.post("/pos/meal-card/charge", {
        provider_id: mealProvider, provider_name: prov?.name || mealProvider,
        brand_code: prov?.brand_code || null, amount: mealCharged, base: mealBase,
        markup_pct: (prov?.markup_pct || 0),
        ref: mealMode === "sms" ? mealCode.trim() : qrRef, channel: mealMode,
      });
      if (!data.approved) {
        toast.error(data.message || "Yemek kartı ödemesi reddedildi. Lütfen tekrar deneyin.", { duration: 6000 });
        return;
      }
      // Entegratör onayladı → kasiyere "Ödeme Alındı" bildirimi
      setMealReceived({ provider_name: data.provider_name, auth_code: data.auth_code,
        masked_pan: data.masked_pan, amount: data.amount });
      toast.success(`✓ Ödeme Alındı · ${data.provider_name}${data.auth_code ? ` · Onay ${data.auth_code}` : ""}`, { duration: 6000 });
      const next = [...collected, {
        method: "meal_card", provider: mealProvider, provider_name: prov?.name || mealProvider,
        currency: "TRY", amount: mealCharged, amountTry: mealCharged, base: mealBase, surcharge: mealSur,
        markup_pct: (prov?.markup_pct || 0), channel: mealMode, ref: mealMode === "sms" ? mealCode.trim() : qrRef,
        auth_code: data.auth_code, masked_pan: data.masked_pan, intent_id: data.intent_id,
      }];
      setCollected(next); persist(next);
      setMealCode("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Yemek kartı entegratörüne ulaşılamadı");
    } finally {
      setMealCharging(false);
    }
  };
  const doCorrect = (i, approver) => {
    const line = collected[i];
    if (!line) return;
    const next = collected.filter((_, idx) => idx !== i);
    setCollected(next); persist(next);
    setCorrections((cs) => [...cs, {
      method: line.method, amount_try: line.amountTry, provider: line.provider_name || null,
      approver: approver?.name || "kendisi", approver_role: approver?.role_label || null,
      at: new Date().toISOString(),
    }]);
    setCorrectIdx(null);
  };
  const requestCorrect = (i) => {
    if (canVoid) return doCorrect(i, null);
    setCorrectIdx(i); setApproveOpen(true);
  };

  const cancelPayment = () => {
    if (onCancel) onCancel(); else onClose();
  };

  const confirm = async () => {
    if (!done || collected.length === 0) return;
    setSubmitting(true);
    const payments = collected.map((c) => (
      c.method === "meal_card"
        ? { method: "meal_card", currency: "TRY", amount: c.amount, provider: c.provider, base: c.base, markup_pct: c.markup_pct }
        : { method: c.method, currency: c.method === "card" ? "TRY" : c.currency, amount: c.amount }
    ));
    try { await onConfirm({ payments, payment_corrections: corrections }); } finally { setSubmitting(false); }
  };

  const METHODS = [["cash", "Nakit", Banknote], ["card", "Kart", CreditCard], ...(mealEnabled ? [["meal_card", "Yemek Kartı", Utensils]] : [])];

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && total === 0) (onClose || onCancel)?.(); }}>
      <DialogContent className="max-w-md max-h-[92vh] overflow-y-auto [&>button.absolute]:hidden" data-testid="payment-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading"><Wallet className="h-5 w-5 text-brand-green" /> Ödeme Al</DialogTitle>
          <DialogDescription>Tutarı yazıp parçayı kaydedin; Kalan otomatik düşer. Yemek kartında komisyon farkı toplama eklenir.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {mealReceived && (
            <div data-testid="meal-payment-received" className="flex items-start gap-2 rounded-md bg-emerald-600 px-3 py-2.5 text-white shadow-sm">
              <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-bold leading-tight">Ödeme Alındı ✓</p>
                <p className="text-[11px] leading-relaxed text-emerald-50">
                  Yemek kartı entegratörü onayladı · <b>{mealReceived.provider_name}</b>
                  {mealReceived.auth_code ? ` · Onay Kodu ${mealReceived.auth_code}` : ""}
                  {mealReceived.masked_pan ? ` · ${mealReceived.masked_pan}` : ""} · {fmtTL(mealReceived.amount)}
                </p>
              </div>
              <button data-testid="meal-received-dismiss" onClick={() => setMealReceived(null)}
                className="shrink-0 rounded px-1 text-emerald-100 hover:text-white">×</button>
            </div>
          )}
          <div className="grid grid-cols-2 gap-2">
            <div className="rounded-md bg-brand-green p-3 text-center text-white">
              <p className="text-[11px] text-emerald-100">Toplam Tutar</p>
              <p data-testid="payment-total" className="tabular font-heading text-2xl font-bold">{fmtTL(effectiveTotal)}</p>
              {mealSurcharge > 0 && <p className="text-[10px] text-emerald-100">mal {fmtTL(total)} + komisyon {fmtTL(mealSurcharge)}</p>}
            </div>
            <div data-testid="pay-remaining" className={`rounded-md p-3 text-center ${!done ? "bg-red-50 ring-1 ring-inset ring-red-200" : "bg-emerald-50 ring-1 ring-inset ring-emerald-200"}`}>
              <p className="text-[11px] text-slate-500">Kalan (tahsil edilecek)</p>
              <p className={`tabular font-heading text-2xl font-bold ${!done ? "text-red-600" : "text-emerald-600"}`}>{fmtTL(remaining)}</p>
            </div>
          </div>

          {rates?.currencies && (
            <div data-testid="pay-fx-strip" className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 rounded-md bg-slate-50 px-3 py-1.5 text-[11px] text-slate-500 ring-1 ring-inset ring-slate-200">
              <span className="font-semibold uppercase tracking-wide text-slate-400">Güncel Kur</span>
              {["USD", "EUR", "GBP"].map((c) => rates.currencies[c] ? (
                <span key={c} data-testid={`pay-fx-${c}`} className="inline-flex items-center gap-1">
                  <b className="text-slate-700">{SYMBOL[c]}1</b>
                  <span className="text-slate-400">=</span>
                  <span className="tabular font-semibold text-brand-green">{fmtTL(rates.currencies[c].used)}</span>
                </span>
              ) : null)}
              {loadingRates && <RefreshCw className="h-3 w-3 animate-spin text-slate-400" />}
            </div>
          )}

          {collected.length > 0 && (
            <div className="space-y-1.5" data-testid="collected-list">
              {collected.map((c, i) => (
                <div key={i} data-testid={`collected-line-${i}`} className="flex items-center justify-between rounded-md bg-emerald-50 px-3 py-2 ring-1 ring-inset ring-emerald-200">
                  <span className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-800">
                    {c.method === "card" ? <CreditCard className="h-4 w-4" /> : c.method === "meal_card" ? <Utensils className="h-4 w-4" /> : <Banknote className="h-4 w-4" />}
                    {c.method === "card" ? `Kart (POS onaylı${c.last4 ? ` ****${c.last4}` : ""})` : c.method === "meal_card" ? `Yemek Kartı · ${c.provider_name}${c.channel === "sms" ? " · SMS/Kod" : " · QR"}` : "Nakit"}
                    <span className="tabular font-bold">{fmtTL(c.amountTry)}</span>
                    {c.method === "meal_card" && c.surcharge > 0 && <span className="text-[11px] font-normal text-emerald-600">(komisyon +{fmtTL(c.surcharge)})</span>}
                    {c.currency && c.currency !== "TRY" && (
                      <span data-testid={`collected-line-${i}-cur`} title="Döviz ile alınan tutar"
                        className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-700 ring-1 ring-inset ring-amber-300">
                        {SYMBOL[c.currency]} {c.currency} · {fmtCur(c.amount, c.currency)}
                      </span>
                    )}
                  </span>
                  <span className="inline-flex items-center gap-2">
                    <span data-testid={`collected-line-${i}-locked`} className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-600" title="Tahsil edilen ödeme silinemez">
                      <Check className="h-3.5 w-3.5" /> Tahsil edildi
                    </span>
                    <button data-testid={`collected-line-${i}-correct`} onClick={() => requestCorrect(i)} title={canVoid ? "Düzelt (yetkilisiniz)" : "Amir onayıyla düzelt"}
                      className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-semibold text-slate-400 transition-colors hover:bg-red-50 hover:text-red-500"><ShieldAlert className="h-3 w-3" /> Düzelt</button>
                  </span>
                </div>
              ))}
            </div>
          )}

          {!done && (
            <div className="rounded-md bg-slate-50 p-3 ring-1 ring-inset ring-slate-200" data-testid="draft-entry">
              <div className="mb-2 flex flex-wrap gap-1.5">
                {METHODS.map(([id, label, Icon]) => (
                  <button key={id} data-testid={`draft-method-${id}`} onClick={() => setD("method", id)}
                    className={`flex min-w-[80px] flex-1 items-center justify-center gap-1.5 rounded-md py-2 text-sm font-semibold ${draft.method === id ? "bg-brand-green text-white" : "bg-white text-slate-600 ring-1 ring-inset ring-slate-200"}`}>
                    <Icon className="h-4 w-4" /> {label}
                  </button>
                ))}
              </div>

              {draft.method === "meal_card" ? (
                <div data-testid="meal-entry" className="space-y-2">
                  <div className="flex gap-1.5">
                    <button type="button" data-testid="meal-mode-qr" onClick={() => setMealMode("qr")}
                      className={`flex flex-1 items-center justify-center gap-1.5 rounded-md py-2 text-xs font-bold ${mealMode === "qr" ? "bg-purple-600 text-white" : "bg-white text-slate-600 ring-1 ring-inset ring-slate-200"}`}>
                      <QrCode className="h-4 w-4" /> QR ile
                    </button>
                    <button type="button" data-testid="meal-mode-sms" onClick={() => setMealMode("sms")}
                      className={`flex flex-1 items-center justify-center gap-1.5 rounded-md py-2 text-xs font-bold ${mealMode === "sms" ? "bg-purple-600 text-white" : "bg-white text-slate-600 ring-1 ring-inset ring-slate-200"}`}>
                      <MessageSquare className="h-4 w-4" /> SMS / Kod ile
                    </button>
                  </div>
                  <select data-testid="meal-provider-select" value={mealProvider} onChange={(e) => setMealProvider(e.target.value)}
                    className="h-10 w-full rounded-md border border-input bg-white px-2 text-sm">
                    {(mealCards?.providers || []).map((p) => <option key={p.id} value={p.id}>{`${p.name} (%${p.markup_pct} komisyon)`}</option>)}
                  </select>
                  <div className="rounded-md bg-white p-2.5 text-sm ring-1 ring-inset ring-slate-200">
                    <div className="flex justify-between text-slate-500"><span>Mal bedeli (kalan)</span><span className="tabular">{fmtTL(mealBase)}</span></div>
                    <div className="flex justify-between text-slate-500"><span>Komisyon farkı (%{(mealMk * 100).toFixed(0)})</span><span className="tabular">+{fmtTL(mealSur)}</span></div>
                    <div className="mt-1 flex justify-between border-t border-dashed border-slate-200 pt-1 font-bold text-slate-800"><span>Karta yansıyacak</span><span className="tabular" data-testid="meal-charged">{fmtTL(mealCharged)}</span></div>
                  </div>
                  {mealMode === "qr" && mealProvider && mealBase > 0 && (
                    <div data-testid="meal-qr-code" className="flex flex-col items-center gap-1 rounded-md bg-white p-3 ring-1 ring-inset ring-purple-200">
                      <QRCodeSVG value={`YEMEKKART-QR|${(mealCards?.providers || []).find((x) => x.id === mealProvider)?.name || mealProvider}|TUTAR:${mealCharged.toFixed(2)} TRY|REF:${qrRef}`} size={132} level="M" />
                      <p className="text-[11px] text-slate-500">Müşteri yemek kartı uygulamasıyla QR'ı okutsun</p>
                    </div>
                  )}
                  {mealMode === "sms" && (
                    <Input data-testid="meal-sms-code" value={mealCode} onChange={(e) => setMealCode(e.target.value)}
                      placeholder="SMS onay kodu / işlem kodu (örn. 6 haneli)" className="tabular" />
                  )}
                  <Button data-testid="draft-collect-meal" onClick={commitMeal} disabled={mealBase <= 0 || !mealProvider || mealCharging || (mealMode === "sms" && !mealCode.trim())}
                    className="h-11 w-full bg-purple-600 text-sm font-bold text-white hover:bg-purple-700">
                    {mealCharging
                      ? <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> Müşterinin ödemesi bekleniyor…</>
                      : <><Utensils className="mr-1.5 h-4 w-4" /> {mealMode === "sms" ? "SMS/Kod ile Tahsil Et" : "QR ile Tahsil Et"} ({fmtTL(mealCharged)})</>}
                  </Button>
                </div>
              ) : (
                <>
                  {draft.method === "cash" && (
                    <div className="mb-2 grid grid-cols-4 gap-1.5">
                      {CURRENCIES.map((c) => (
                        <button key={c} data-testid={`draft-cur-${c}`} onClick={() => setD("currency", c)}
                          className={`rounded-md py-1.5 text-xs font-bold ${draft.currency === c ? "bg-brand-orange text-white" : "bg-white text-slate-600 ring-1 ring-inset ring-slate-200"}`}>
                          {SYMBOL[c]} {c}
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <div className="relative flex-1">
                      <Input data-testid="draft-amount" type="number" value={draft.amount} autoFocus
                        onChange={(e) => setD("amount", e.target.value)}
                        placeholder={draft.method === "card" ? "Karttan çekilecek tutar" : "Alınan tutar (tümü ya da bir kısmı)"} className="tabular pr-16" />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">{SYMBOL[draft.method === "card" ? "TRY" : draft.currency]}</span>
                    </div>
                    <button data-testid="draft-fill-remaining" onClick={fillRemaining} title="Kalan tutarın tamamını yaz"
                      className="whitespace-nowrap rounded-md bg-slate-200 px-2.5 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-300">Kalanı yaz</button>
                  </div>
                  {draft.method === "cash" && draft.currency !== "TRY" && draftAmt > 0 && (
                    <p className="mt-1.5 text-[11px] text-slate-500">Kur: <b className="tabular">{rateOf(draft.currency) ? rateOf(draft.currency).toFixed(2) : "—"} ₺</b> · ≈ <b className="tabular">{fmtTL(draftTry)}</b></p>
                  )}
                  <div className="mt-2.5">
                    {draft.method === "cash" ? (
                      <Button data-testid="draft-collect-cash" onClick={commitCash} disabled={draftTry <= 0}
                        className="h-11 w-full bg-brand-green text-sm font-bold text-white hover:bg-brand-green-600">
                        <DoorOpen className="mr-1.5 h-4 w-4" /> Nakit Al & Kasa Aç{draftTry > 0 ? ` (${fmtTL(draftTry)})` : ""}
                      </Button>
                    ) : (
                      <Button data-testid="draft-collect-card" onClick={commitCard} disabled={draftTry <= 0 || posSending}
                        className="h-11 w-full bg-slate-900 text-sm font-bold text-white hover:bg-slate-800">
                        {posSending ? <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> POS cihazına gönderiliyor…</> : <><CreditCard className="mr-1.5 h-4 w-4" /> POS'a Gönder{draftTry > 0 ? ` (${fmtTL(draftTry)})` : ""}</>}
                      </Button>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          <button onClick={loadRates} className="ml-auto flex items-center gap-1 text-[11px] text-slate-400 hover:text-brand-green">
            <RefreshCw className={`h-3 w-3 ${loadingRates ? "animate-spin" : ""}`} /> kuru yenile
          </button>

          <div className={`rounded-md p-3 text-center ${done ? "bg-emerald-50 ring-1 ring-inset ring-emerald-200" : "bg-slate-50 ring-1 ring-inset ring-slate-200"}`} data-testid="pay-summary">
            <p className="text-[11px] text-slate-500">Tahsil edilen: <b className="tabular">{fmtTL(round2(cardTry + qrTry + cashTry + mealBaseTry + mealSurcharge))}</b>{collected.length > 1 ? " (karma)" : ""}</p>
            <p className="text-xs text-slate-500">Para Üstü (nakitten, TL)</p>
            <p data-testid="pay-change" className={`tabular font-heading text-2xl font-bold ${change > 0 ? "text-brand-orange" : "text-slate-700"}`}>{fmtTL(change)}</p>
          </div>

          <Button data-testid="payment-confirm" onClick={confirm} disabled={submitting || !done || collected.length === 0 || posSending}
            className="h-12 w-full bg-brand-orange text-base font-bold text-white hover:bg-orange-600">
            {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Check className="mr-2 h-5 w-5" />}
            {done ? "Ödemeyi Tamamla" : `Kalan ${fmtTL(remaining)} — tahsil edin`}
          </Button>
          {collected.length === 0 ? (
            <button data-testid="payment-cancel" onClick={cancelPayment} className="w-full text-center text-xs text-slate-400 transition-colors hover:text-red-500">Vazgeç / kapat</button>
          ) : (
            <p data-testid="payment-locked-note" className="text-center text-[11px] font-medium text-slate-500">Tahsilat başladı — alınan ödemeler <b>silinemez</b>; yanlış giriş yalnız <b>amir onayıyla</b> düzeltilebilir.</p>
          )}
          <p className="text-center text-[10px] text-slate-400">{(getDevice()?.bankPos?.enabled && getDevice()?.bankPos?.simulate !== true) ? "Kart tutarı bağlı banka POS cihazına gönderilir; onay/red kasaya döner." : "Kart tutarı banka POS cihazına gönderilir (şu an Simülasyon modu — Cihazlar'dan gerçek cihazı bağlayın)."}</p>
        </div>
        <ApprovalDialog open={approveOpen} onClose={() => { setApproveOpen(false); setCorrectIdx(null); }}
          permission="sale.void.approve" title="Ödeme Düzeltme Onayı"
          description="Alınan bir ödemeyi düzeltmek için yetkili (amir) onayı gerekir. İşlem iz kaydına yazılır."
          onApproved={(_cred, approver) => { if (correctIdx !== null) doCorrect(correctIdx, approver); }} />
      </DialogContent>
    </Dialog>
  );
}
